import { useRef, useState, type MouseEvent, type ReactNode } from 'react';
import Button, { type ButtonProps } from '~/components/new/Button';
import Modal, { ModalAPI } from '~/components/ui/Modal';

type ConfirmCallback<T> = (result: boolean, data?: T) => void;

export default function useConfirm<T>(onConfirm: ConfirmCallback<T>, props: Partial<ConfirmProps>) {
    const [_show, setShow] = useState(false);
    const [data, setData] = useState<T>(props.data as T);

    return {
        data,
        show: (data?: T) => {
            if (data !== undefined) setData(data);
            setShow(true);
        },
        render: (children: ReactNode) => {
            if (!_show) return null;

            return (
                <Confirm
                    {...props}
                    usePortal
                    onClose={() => {
                        props?.onClose?.();
                        setShow(false);
                    }}
                    data={data}
                    onConfirm={onConfirm}
                    data-test-component='ui/Confirm'>
                    {children}
                </Confirm>
            );
        },
    };
}

export function useExitConfirm(onConfirm: ConfirmCallback<void>) {
    const confirm = useConfirm(onConfirm, {
        acceptLabel: 'Yes, discard changes',
        acceptAction: 'negative',
        acceptVariant: 'filled-light',
    });
    return {
        ...confirm,
        render: (children?: ReactNode) =>
            confirm.render(
                <Modal.BigIconContent
                    icon='warn'
                    title='Are you sure you want to exit?'
                    description='Any unsaved changes will be lost.'>
                    {children}
                </Modal.BigIconContent>,
            ),
    };
}

export function useDangerConfirm<T>(onConfirm: ConfirmCallback<T>, props: Partial<ConfirmProps>) {
    const confirm = useConfirm(onConfirm, {
        acceptAction: 'negative',
        ...props,
    });
    return {
        ...confirm,
        render: (title: string, description: string, children?: ReactNode) =>
            confirm.render(
                <Modal.BigIconContent
                    icon='warn'
                    title={title}
                    description={description}>
                    {children}
                </Modal.BigIconContent>,
            ),
    };
}

export function useInfoConfirm<T>(onConfirm: ConfirmCallback<T>, props: Partial<ConfirmProps>) {
    const confirm = useConfirm(onConfirm, props);
    return {
        ...confirm,
        render: (title: string, description?: string, children?: ReactNode) =>
            confirm.render(
                <Modal.BigIconContent
                    title={title}
                    description={description}>
                    {children}
                </Modal.BigIconContent>,
            ),
    };
}

export type ConfirmProps<T = object> = {
    onClose: () => void;
    title?: string;
    acceptLabel?: string;
    cancelLabel?: string;
    acceptAction?: ButtonProps['action'];
    acceptVariant?: ButtonProps['variant'];
    cancelAction?: ButtonProps['action'];
    cancelVariant?: ButtonProps['variant'];
    acceptDisabled?: boolean;
    usePortal?: boolean;
    children: ReactNode;
    data?: T;
    onConfirm: ConfirmCallback<T>;
    className?: string;
    buttonsAlign?: 'sides' | 'center';
};

function Confirm<T>({
    onClose,
    title = '',
    acceptLabel = 'Accept',
    cancelLabel = 'Cancel',
    acceptAction = 'primary',
    acceptVariant = 'filled',
    cancelAction = 'primary',
    cancelVariant = 'floating',
    acceptDisabled,
    usePortal = false,
    children,
    data,
    onConfirm,
    className,
}: ConfirmProps<T>) {
    const modalRef = useRef<ModalAPI>(null!);
    return (
        <Modal
            ref={modalRef}
            size='small'
            className={className}
            usePortal={usePortal}
            onClose={onClose}>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Body className='gap-2'>{children}</Modal.Body>
            <Modal.Footer>
                {cancelLabel && (
                    <Button
                        data-action='cancel'
                        variant={cancelVariant}
                        action={cancelAction}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            void (async () => {
                                await modalRef.current.close();
                                onConfirm(false, data);
                            })();
                        }}
                        data-test-name='cancel'>
                        {cancelLabel}
                    </Button>
                )}
                {acceptLabel && (
                    <Button
                        disabled={acceptDisabled}
                        action={acceptAction}
                        variant={acceptVariant}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            void (async () => {
                                await modalRef.current.close();
                                onConfirm(true, data);
                            })();
                        }}
                        data-test-name='accept'>
                        {acceptLabel}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
